import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface FileUpload {
  file: File
  mood: string
  stemType: string
  power: number
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress?: number
  error?: string
}

interface AudioFile {
  id: number
  filename: string
  original_name: string
  mood: string
  stem_type: string
  power: number
  variation: number
  file_size: number
  mime_type: string
  uploaded_at: string
}

export function AdminUpload() {
  const { user } = useAuth()
  const [uploads, setUploads] = useState<FileUpload[]>([])
  const [files, setFiles] = useState<AudioFile[]>([])
  const [dragActive, setDragActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const moods = ['base', 'calm', 'energetic', 'hyper', 'serious']
  const stemTypes = ['main', 'bass', 'chord', 'layer', 'sfx']
  const powerLevels = [1, 2, 3]

  // Fetch existing files
  useEffect(() => {
    if (user?.brandId) {
      fetchFiles()
    }
  }, [user])

  const fetchFiles = async () => {
    try {
      setIsLoading(true)
      const response = await fetch('http://localhost:3000/api/files', {
        credentials: 'include'
      })
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please log in to view files')
        }
        throw new Error('Failed to fetch files')
      }
      const data = await response.json()
      setFiles(data.files)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch files')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files))
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(Array.from(e.target.files))
    }
  }

  const handleFiles = (files: File[]) => {
    const newUploads = files.map(file => ({
      file,
      mood: 'base',
      stemType: 'main',
      power: 1,
      status: 'pending' as const
    }))
    setUploads(prev => [...prev, ...newUploads])
  }

  const handleUpload = async (upload: FileUpload) => {
    if (!user?.brandId) {
      setError('Please log in to upload files')
      return
    }

    const formData = new FormData()
    formData.append('file', upload.file)
    formData.append('mood', upload.mood)
    formData.append('stemType', upload.stemType)
    formData.append('power', upload.power.toString())

    console.log('Uploading file:', {
      filename: upload.file.name,
      mood: upload.mood,
      stemType: upload.stemType,
      power: upload.power
    })

    try {
      setUploads(prev => 
        prev.map(u => 
          u === upload ? { ...u, status: 'uploading' } : u
        )
      )

      const response = await fetch('https://tu-du-dum.com/api/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      console.log('Upload response status:', response.status)
      const responseText = await response.text()
      console.log('Upload response text:', responseText)

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please log in to upload files')
        }
        try {
          const error = JSON.parse(responseText)
          throw new Error(error.error || 'Upload failed')
        } catch (e) {
          throw new Error('Upload failed: ' + responseText)
        }
      }

      const data = JSON.parse(responseText)
      console.log('Upload successful:', data)

      setUploads(prev => 
        prev.map(u => 
          u === upload ? { ...u, status: 'success' } : u
        )
      )

      // Refresh file list after successful upload
      fetchFiles()
    } catch (error) {
      console.error('Upload error:', error)
      setUploads(prev => 
        prev.map(u => 
          u === upload ? { ...u, status: 'error', error: error instanceof Error ? error.message : 'Upload failed' } : u
        )
      )
    }
  }

  const handleDelete = async (file: AudioFile) => {
    if (!user?.brandId) {
      setError('Please log in to delete files')
      return
    }

    if (!confirm(`Are you sure you want to delete ${file.original_name}?`)) {
      return
    }

    try {
      const response = await fetch(`http://localhost:3000/api/files/${file.id}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please log in to delete files')
        }
        throw new Error('Failed to delete file')
      }

      // Refresh file list after successful deletion
      fetchFiles()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete file')
    }
  }

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024)
    return `${mb.toFixed(2)} MB`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const updateUpload = (upload: FileUpload, updates: Partial<FileUpload>) => {
    setUploads(prev => 
      prev.map(u => 
        u === upload ? { ...u, ...updates } : u
      )
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Upload Sounds</h1>

      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 mb-6 text-center ${
          dragActive ? 'border-green-500 bg-green-50' : 'border-gray-300'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
          accept="audio/*"
        />
        <label
          htmlFor="file-upload"
          className="cursor-pointer text-gray-600"
        >
          <div className="text-4xl mb-2">📁</div>
          <p>Drag and drop audio files here or click to browse</p>
        </label>
      </div>

      {/* Upload list */}
      <div className="space-y-4 mb-8">
        {uploads.map((upload, index) => (
          <div
            key={index}
            className="border rounded-lg p-4 flex items-center gap-4"
          >
            <div className="flex-1">
              <p className="font-medium">{upload.file.name}</p>
              <p className="text-sm text-gray-500">
                {formatFileSize(upload.file.size)}
              </p>
            </div>

            <div className="flex gap-2">
              <select
                value={upload.mood}
                onChange={e => updateUpload(upload, { mood: e.target.value })}
                className="border rounded px-2 py-1"
              >
                {moods.map(mood => (
                  <option key={mood} value={mood}>{mood}</option>
                ))}
              </select>

              <select
                value={upload.stemType}
                onChange={e => updateUpload(upload, { stemType: e.target.value })}
                className="border rounded px-2 py-1"
              >
                {stemTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <select
                value={upload.power}
                onChange={e => updateUpload(upload, { power: Number(e.target.value) })}
                className="border rounded px-2 py-1"
              >
                {powerLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>

              {upload.status === 'pending' && (
                <button
                  onClick={() => handleUpload(upload)}
                  className="px-4 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Upload
                </button>
              )}

              {upload.status === 'uploading' && (
                <div className="text-blue-500">Uploading...</div>
              )}

              {upload.status === 'success' && (
                <div className="text-green-500">✓</div>
              )}

              {upload.status === 'error' && (
                <div className="text-red-500" title={upload.error}>❌</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* File list */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">Uploaded Files</h2>
        
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          </div>
        ) : files.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No files uploaded yet</p>
        ) : (
          <div className="space-y-4">
            {files.map(file => (
              <div
                key={file.id}
                className="border rounded-lg p-4 flex items-center gap-4"
              >
                <div className="flex-1">
                  <p className="font-medium">{file.original_name}</p>
                  <p className="text-sm text-gray-500">
                    {formatFileSize(file.file_size)} • {formatDate(file.uploaded_at)}
                  </p>
                  <p className="text-sm text-gray-600">
                    {file.mood} • {file.stem_type} • Power: {file.power} • Variation: {file.variation}
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <audio
                    controls
                    className="w-64"
                    src={`/stems/${file.filename}`}
                  >
                    Your browser does not support the audio element.
                  </audio>

                  <button
                    onClick={() => handleDelete(file)}
                    className="px-3 py-1 text-red-600 hover:text-red-800"
                    title="Delete file"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
} 